import { EmblaCarouselType } from 'embla-carousel'
import { useCallback, useEffect, useEffectEvent, useState } from 'react'

type UseAutoplayType = {
  autoplayIsPlaying: boolean
  toggleAutoplay: () => void
  onAutoplayButtonClick: (_callback: () => void) => void
}

export const useAutoplay = (
  emblaApi: EmblaCarouselType | undefined
): UseAutoplayType => {
  const [autoplayIsPlaying, setAutoplayIsPlaying] = useState(false)

  const onAutoplayButtonClick = useCallback(
    (callback: () => void) => {
      const autoplay = emblaApi?.plugins()?.autoplay
      if (!autoplay) {
        return
      }

      const resetOrStop =
        autoplay.options.stopOnInteraction === false
          ? autoplay.reset
          : autoplay.stop

      resetOrStop()
      callback()
    },
    [emblaApi]
  )

  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) {
      return
    }

    const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play
    playOrStop()
  }, [emblaApi])

  const onAutoplayButtonClickEvent = useEffectEvent((play: boolean) => {
    setAutoplayIsPlaying(play)
  })

  useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay
    if (!autoplay) {
      return
    }

    onAutoplayButtonClickEvent(autoplay.isPlaying())
    emblaApi
      .on('autoplay:play', () => onAutoplayButtonClickEvent(true))
      .on('autoplay:stop', () => onAutoplayButtonClickEvent(false))
      .on('reInit', () => onAutoplayButtonClickEvent(autoplay.isPlaying()))
  }, [emblaApi])

  return {
    autoplayIsPlaying,
    toggleAutoplay,
    onAutoplayButtonClick
  }
}
